import React, { useState, useEffect, useRef } from "react";
import FormSelect from "../../common/components/FormSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import BrandLoader from "../../common/components/BrandLoader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import * as XLSX from "xlsx";
import invoke from "../../common/utils/invoke";
import Modal from '../../common/components/Modal';
import "./NavFundDetailsForm.scss";


/** If NAV value has exactly 3 decimal places, append "0" for display (e.g. 43.677 → 43.6770). */
const formatNavValueDisplay = (val) => {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  if (!s) return "";
  if (/^-?\d+\.\d{3}$/.test(s)) return s + "0";
  return s;
};

/** Normalize NAV in each row so 3-decimal values show as 4 decimals everywhere (chart, export, tooltip). */
const normalizeNavDetailItem = (row) => {
  const raw = row.navValue ?? row.nav;
  const normalized = formatNavValueDisplay(raw);
  return {
    ...row,
    navValue:
      normalized !== ""
        ? normalized
        : raw != null && raw !== ""
          ? String(raw)
          : "",
  };
};

/** Format YYYY-MM-DD for filename (dd-mm-yyyy). */
const formatNavDateForFilename = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  }
  return dateStr;
};

/** Parse YYYY-MM-DD to JS Date for Excel (cell value). Returns null if invalid. */
const parseNavDateToDate = (dateStr) => {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr).trim()))
    return null;
  const [y, m, d] = dateStr.trim().split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
};

/** Convert YYYY-MM-DD to Excel date serial (integer, date-only, no time). Returns null if invalid. */
const navDateToExcelSerial = (dateStr) => {
  const date = parseNavDateToDate(dateStr);
  if (!date) return null;
  const excelEpoch = new Date(1899, 11, 30);
  return Math.round(
    (date.getTime() - excelEpoch.getTime()) / (24 * 60 * 60 * 1000),
  );
};

/** Excel display format: 25-Feb-26. Cell value should be serial (integer) so Excel shows date only, no time. */
const EXCEL_DATE_FORMAT = "dd-mmm-yy";

/** Apply date format to date column in sheet (column 0, data rows 1..rowCount). */
const applyExcelDateFormat = (ws, dateColIndex, rowCount) => {
  for (let r = 1; r <= rowCount; r++) {
    const ref = XLSX.utils.encode_cell({ r, c: dateColIndex });
    if (ws[ref] && ws[ref].t !== "s") ws[ref].z = EXCEL_DATE_FORMAT;
  }
};

const CATEGORY_OPEN = "Open";
const CATEGORY_TRADITIONAL = "Traditional";

/** Value for "All Funds" option in Fund Name dropdown (Open category). */
const ALL_FUNDS_VALUE_OPEN = "__all_funds_open__";
/** Value for "All Funds" option in Fund Name dropdown (Traditional category). */
const ALL_FUNDS_VALUE_TRADITIONAL = "__all_funds_traditional__";

const isAllFundsValue = (value) =>
  value === ALL_FUNDS_VALUE_OPEN || value === ALL_FUNDS_VALUE_TRADITIONAL;

/** Order for Open Group By options derived from fundCategory (NULL → All). Traditional is excluded. */
const OPEN_GROUP_BY_ORDER = ["All", "Hybrid", "Fixed Income", "Equity"];

/** Normalize API fundCategory: NULL/null/undefined → "All". */
const normalizeFundCategory = (fundCategory) => {
  const v =
    fundCategory == null || String(fundCategory).toUpperCase() === "NULL"
      ? "All"
      : String(fundCategory).trim();
  return v || "All";
};

const today = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const oneMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });


/** Filter NAV detail rows to only those whose fundCode is in the given set (for "All Funds" by category). */
const filterItemsByFundCodes = (items, fundCodesSet) => {
  if (!Array.isArray(items) || !fundCodesSet || fundCodesSet.size === 0)
    return items || [];
  return items.filter((row) =>
    fundCodesSet.has(String(row.fundCode || "").trim()),
  );
};

/** Deduplicate NAV rows by date only (keep first occurrence per date). Used for single-fund chart. */
const dedupeNavByDate = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Set();
  return items.filter((row) => {
    const key = String(row.navDate || row.date || "").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Deduplicate NAV rows by fundCode + navDate (one row per fund per date). Used for All Funds export. */
const dedupeNavByFundAndDate = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Set();
  return items.filter((row) => {
    const code = String(row.fundCode || "").trim();
    const d = String(row.navDate || row.date || "").trim();
    const key = `${code}|${d}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Enrich NAV rows with fund name and SFIN from masters when row has them empty (for tooltip/display). */
const enrichNavRowsWithMasters = (rows, masters) => {
  if (!Array.isArray(rows) || !Array.isArray(masters) || masters.length === 0)
    return rows;
  const codeToMaster = new Map(
    masters
      .filter((i) => String(i.fundReferenceCode || i.fundCode || "").trim())
      .map((i) => [
        String(i.fundReferenceCode || i.fundCode || "").trim(),
        {
          name: i.fullName || i.name || "",
          sfin: i.sFIN ?? i.sfin ?? "",
        },
      ]),
  );
  return rows.map((row) => {
    const code = String(row.fundCode || "").trim();
    const master = codeToMaster.get(code);
    if (!master) return row;
    const fundName =
      String(row.fundName ?? "").trim() || master.name || row.fundName;
    const sfinNo =
      String(row.sfinNo ?? row.sfin ?? "").trim() || master.sfin || row.sfinNo;
    return { ...row, fundName, sfinNo };
  });
};

const NavFundDetailsForm = () => {
  const [category, setCategory] = useState(CATEGORY_OPEN);
  const [groupBySelected, setGroupBySelected] = useState(["All"]);
  const [fundName, setFundName] = useState("");
  const [dateFrom, setDateFrom] = useState(oneMonthAgo);
  const [dateTo, setDateTo] = useState(() => today());
  const [chartData, setChartData] = useState([]);
  const [selectedFundLabel, setSelectedFundLabel] = useState("");
  const [loading, setLoading] = useState(false);
  /** Masters per tab; both loaded once on mount (server ?category=) so switching Open/Traditional stays instant like the original single-fetch UX. */
  const [openMasters, setOpenMasters] = useState([]);
  const [traditionalMasters, setTraditionalMasters] = useState([]);
  const [fundsLoading, setFundsLoading] = useState(true);

  const tabMasters = React.useMemo(
    () =>
      category === CATEGORY_OPEN ? openMasters : traditionalMasters,
    [category, openMasters, traditionalMasters],
  );
  const [chartError, setChartError] = useState("");
  const initialLoadDone = useRef(false);
  const [showPopup, setShowPopup] = useState(false);
  /** Raw NAV detail rows when "All Funds" is selected (for Excel export: one row per fund per date with fund name and SFIN). */
  const [allFundsNavDetails, setAllFundsNavDetails] = useState([]);

  const showGroupBy = category === CATEGORY_OPEN;

  // Group By options for Open: always derived from Open masters (same as original openItems from full list), even when Traditional tab is selected.
  const groupByOptions = React.useMemo(() => {
    const categories = [
      ...new Set(
        openMasters.map((item) => normalizeFundCategory(item.fundCategory)),
      ),
    ];
    return OPEN_GROUP_BY_ORDER.filter((value) =>
      categories.includes(value),
    ).map((value) => ({ value, label: value }));
  }, [openMasters]);

  // Fund options: "All Funds" first, then by category and (for Open) by selected Group By filters
  const fundOptions = React.useMemo(() => {
    const allFundsOption =
      category === CATEGORY_OPEN
        ? {
            value: ALL_FUNDS_VALUE_OPEN,
            label: "All Funds",
            item: null,
          }
        : {
            value: ALL_FUNDS_VALUE_TRADITIONAL,
            label: "All Funds",
            item: null,
          };
    const raw = tabMasters;
    const toOption = (item) => {
      const name = item.fullName || item.name || "";
      const sfin = item.sFIN ?? item.sfin ?? "";
      const label = sfin ? `${name} (${sfin})` : name || "—";
      const value = String(
        item.id ??
          item.fundNo ??
          item.fundReferenceCode ??
          item.sFIN ??
          item.sfin ??
          "",
      );
      return { value, label, item };
    };
    let individualOptions;
    if (category !== CATEGORY_OPEN) {
      individualOptions = raw.map(toOption);
    } else {
      // Open: when nothing selected (e.g. All deselected), show no individual funds; when only "All" or all options selected, show all; else filter by selected categories
      if (groupBySelected.length === 0) {
        return [allFundsOption];
      }
      const effective = groupBySelected;
      const onlyAllSelected = effective.length === 1 && effective[0] === "All";
      const allOptionsSelected =
        groupByOptions.length > 0 &&
        groupByOptions.every((o) => effective.includes(o.value));
      const showAllOpen = onlyAllSelected || allOptionsSelected;
      const filtered = showAllOpen
        ? raw
        : raw.filter((item) =>
            effective.includes(normalizeFundCategory(item.fundCategory)),
          );
      individualOptions = filtered.map(toOption);
    }
    return [allFundsOption, ...individualOptions];
  }, [category, tabMasters, groupBySelected, groupByOptions]);

  /** Format YYYY-MM-DD to axis format dd.mm.yy */
  const navDateToAxisFormat = (navDateStr) => {
    if (!navDateStr) return "";
    const parts = String(navDateStr).trim().split("-");
    if (parts.length !== 3) return navDateStr;
    const [y, m, d] = parts;
    const yearShort = y.length === 4 ? y.slice(-2) : y;
    return `${d}.${m}.${yearShort}`;
  };

  /** Normalize navDate (YYYY-MM-DD or DD.MM.YY / D.M.YY) to YYYYMMDD for correct sort order. Safe for any input. */
  const navDateToSortKey = (navDateStr) => {
    try {
      if (navDateStr == null) return "";
      const s = String(navDateStr).trim();
      if (!s || s === "null" || s === "undefined") return "";
      const iso = s.slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso.replace(/-/g, "");
      const ddmmyy = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
      if (ddmmyy) {
        const [, d, m, y] = ddmmyy;
        const year = y.length === 2 ? `20${y}` : y;
        return `${year}${m.padStart(2, "0")}${d.padStart(2, "0")}`;
      }
      return s;
    } catch {
      return "";
    }
  };

  const formatDateToYYYYMMDD = (d) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    let cancelled = false;
    const loadOne = async (cat) => {
      const maxAttempts = 2;
      let items = [];
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const response = await invoke.fetchNavFundMasters(cat);
        items = Array.isArray(response) ? response : [];
        if (items.length > 0 || attempt === maxAttempts) {
          break;
        }
        await sleep(800);
      }
      return items;
    };
    const load = async () => {
      setFundsLoading(true);
      try {
        const [open, traditional] = await Promise.all([
          loadOne(CATEGORY_OPEN),
          loadOne(CATEGORY_TRADITIONAL),
        ]);
        if (cancelled) return;
        setOpenMasters(open);
        setTraditionalMasters(traditional);
      } catch {
        if (!cancelled) {
          setOpenMasters([]);
          setTraditionalMasters([]);
        }
      } finally {
        if (!cancelled) setFundsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // When category changes, default Fund Name selection to first individual fund (second option after "All Funds")
  useEffect(() => {
    if (fundsLoading || fundOptions.length === 0) return;
    const firstFundOption =
      fundOptions.length > 1 ? fundOptions[1] : fundOptions[0];
    setFundName(firstFundOption.value);
    setSelectedFundLabel(firstFundOption.label);
  }, [category]);

  // Initial load: set default fund to first individual fund (second option after "All Funds"), then load chart
  useEffect(() => {
    if (fundsLoading || fundOptions.length === 0 || initialLoadDone.current)
      return;
    initialLoadDone.current = true;
    if (category === CATEGORY_OPEN && groupByOptions.length > 0) {
      setGroupBySelected(groupByOptions.map((o) => o.value));
    }
    const defaultOption =
      fundOptions.length > 1 ? fundOptions[1] : fundOptions[0];
    setFundName(defaultOption.value);
    setSelectedFundLabel(defaultOption.label);

    // Load chart for default selected (All Funds or single fund) and default date range on page load
    const isAllFunds = isAllFundsValue(defaultOption.value);
    const fundCode = isAllFunds
      ? ""
      : defaultOption.item?.fundReferenceCode ||
        defaultOption.item?.fundCode ||
        "";
    setLoading(true);
    setChartError("");
    const dateFromStr = formatDateToYYYYMMDD(oneMonthAgo());
    const dateToStr = formatDateToYYYYMMDD(today());
    const mastersForAllFundsSnapshot =
      category === CATEGORY_OPEN ? openMasters : traditionalMasters;
    invoke
      .fetchNavFundDetails(fundCode || null, dateFromStr, dateToStr)
      .then((rawResponse) => {
        const items = Array.isArray(rawResponse)
          ? rawResponse.map(normalizeNavDetailItem)
          : [];
        try {
          if (isAllFunds) {
            const masters = mastersForAllFundsSnapshot;
            const fundCodesSet = new Set(
              masters
                .map((i) =>
                  String(i.fundReferenceCode || i.fundCode || "").trim(),
                )
                .filter(Boolean),
            );
            const filtered = filterItemsByFundCodes(items, fundCodesSet);
            const enriched = enrichNavRowsWithMasters(filtered, masters);
            const deduped = dedupeNavByFundAndDate(enriched);
            setAllFundsNavDetails(deduped);
            setChartData([]);
            if (deduped.length === 0) {
              setShowPopup(true);
              setChartError(
                "No NAV data found for the selected date range. Try a different date range.",
              );
            } else {
              setChartError("");
            }
          } else {
            setAllFundsNavDetails([]);
            const sorted = [...items].sort((a, b) =>
              navDateToSortKey(b.navDate || b.date).localeCompare(
                navDateToSortKey(a.navDate || a.date),
                undefined,
                { numeric: true },
              ),
            );
            const dedupedByDate = dedupeNavByDate(sorted);
            const chartDataFromApi = dedupedByDate.map((row) => ({
              date: navDateToAxisFormat(row.navDate) || row.navDate,
              nav: parseFloat(row.navValue) || 0,
              navDateRaw: row.navDate,
              navValueOriginal: row.navValue,
            }));
            setChartData(chartDataFromApi);
            if (chartDataFromApi.length === 0) {
              setChartError(
                "No NAV data found for the selected fund and date range. Try a different fund or dates.",
              );
            } else {
              setChartError("");
            }
          }
        } catch (err) {
          setChartData([]);
          setAllFundsNavDetails([]);
          setChartError("Unable to load NAV data. Please try again.");
        }
      })
      .catch(() => {
        setChartData([]);
        setAllFundsNavDetails([]);
        setChartError("Unable to load NAV data. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [fundsLoading, fundOptions, category, groupByOptions]);

  // When category or Group By changes, ensure selected fund is still in the list
  // useEffect(() => {
  //   if (fundsLoading || fundOptions.length === 0) return;
  //   const currentInList = fundOptions.some((o) => o.value === fundName);
  //   if (!currentInList) {
  //     const first = fundOptions[0];
  //     if (first) {
  //       setFundName(first.value);
  //       setSelectedFundLabel(first.label);
  //     }
  //   }
  // }, [category, groupBySelected, fundOptions, fundsLoading]);

  const toggleGroupBy = (value) => {
    setGroupBySelected((prev) => {
      const isChecking = !prev.includes(value);
      const next = isChecking
        ? [...prev, value]
        : prev.filter((v) => v !== value);
      // If "All" is checked, all other options under Open should be yes (select all)
      if (value === "All" && isChecking) {
        return groupByOptions.map((o) => o.value);
      }
      // If "All" is unchecked, deselect all options (Hybrid, Fixed Income, Equity too)
      if (value === "All" && !isChecking) {
        return [];
      }
      // When toggling another option: keep "All" only if all others are still selected
      const others = OPEN_GROUP_BY_ORDER.filter((v) => v !== "All");
      const hasAllOthers = others.every((v) => next.includes(v));
      if (next.includes("All") && !hasAllOthers) {
        return next.filter((v) => v !== "All");
      }
      if (!next.includes("All") && hasAllOthers) {
        return [...next, "All"];
      }
      return next;
    });
  };

  const handleGo = async () => {
    setChartError("");
    setSelectedFundLabel(
      fundOptions.find((f) => f.value === fundName)?.label || "Fund",
    );
    const selectedFund = fundOptions.find((f) => f.value === fundName)?.item;
    const isAllFunds = isAllFundsValue(fundName);
    const fundCode = isAllFunds
      ? ""
      : selectedFund?.fundReferenceCode || selectedFund?.fundCode || "";

    if (!isAllFunds && (!fundCode || !String(fundCode).trim())) {
      setChartError("Please select a fund.");
      setChartData([]);
      return;
    }

    setLoading(true);
    const dateFromStr = formatDateToYYYYMMDD(dateFrom);
    const dateToStr = formatDateToYYYYMMDD(dateTo);

    try {
      const rawResponse = await invoke.fetchNavFundDetails(
        fundCode || null,
        dateFromStr,
        dateToStr,
      );
      const items = Array.isArray(rawResponse)
        ? rawResponse.map(normalizeNavDetailItem)
        : [];
      if (isAllFunds) {
        const masters = tabMasters;
        const fundCodesSet = new Set(
          masters
            .map((i) => String(i.fundReferenceCode || i.fundCode || "").trim())
            .filter(Boolean),
        );
        const filtered = filterItemsByFundCodes(items, fundCodesSet);
        const enriched = enrichNavRowsWithMasters(filtered, masters);
        const deduped = dedupeNavByFundAndDate(enriched);
        setAllFundsNavDetails(deduped);
        setChartData([]);
        if (deduped.length === 0) {
          setChartError(
            "No NAV data found for the selected date range. Try a different date range.",
          );
        } else {
          setChartError("");
        }
      } else {
        setAllFundsNavDetails([]);
        const sorted = [...items].sort((a, b) =>
          navDateToSortKey(b.navDate || b.date).localeCompare(
            navDateToSortKey(a.navDate || a.date),
            undefined,
            { numeric: true },
          ),
        );
        const dedupedByDate = dedupeNavByDate(sorted);
        const chartDataFromApi = dedupedByDate.map((row) => ({
          date: navDateToAxisFormat(row.navDate) || row.navDate,
          nav: parseFloat(row.navValue) || 0,
          navDateRaw: row.navDate,
          navValueOriginal: row.navValue,
        }));
        setChartData(chartDataFromApi);
        if (chartDataFromApi.length === 0) {
          setChartError(
            "No NAV data found for the selected fund and date range. Try a different fund or dates.",
          );
        } else {
          setChartError("");
        }
      }
    } catch {
      setChartData([]);
      setAllFundsNavDetails([]);
      setChartError("Unable to load NAV data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleExcel = () => {
    const isAllFunds = isAllFundsValue(fundName);
    if (
      (isAllFunds && !allFundsNavDetails.length) ||
      (!isAllFunds && !chartData.length)
    ) {
      setShowPopup(true);
      return;
    }
    if (isAllFunds) {
      if (!allFundsNavDetails.length) return;
      const masters = tabMasters;
      const codeToMaster = new Map(
        masters
          .map((i) => {
            const code = String(i.fundReferenceCode || i.fundCode || "").trim();
            return [
              code,
              {
                name: i.fullName || i.name || "",
                sfin: i.sFIN ?? i.sfin ?? "",
              },
            ];
          })
          .filter(([code]) => code),
      );
      const headers = ["Nav Date", "Fund Name", "SFIN", "NAV"];
      const sorted = [...allFundsNavDetails].sort((a, b) => {
        const dateCmp = String(b.navDate || "").localeCompare(
          String(a.navDate || ""),
          undefined,
          { numeric: true },
        );
        if (dateCmp !== 0) return dateCmp;
        return String(a.fundName || "").localeCompare(String(b.fundName || ""));
      });
      const rows = sorted.map((row) => {
        const code = String(row.fundCode || "").trim();
        const master = codeToMaster.get(code);
        const displayName =
          String(row.fundName || "").trim() || master?.name || "—";
        const displaySfin =
          String(row.sfinNo ?? row.sfin ?? "").trim() ||
          (master ? String(master.sfin || "").trim() : "") ||
          "—";
        const navNum =
          typeof row.navValue === "number"
            ? row.navValue
            : parseFloat(row.navValue ?? row.nav) || 0;
        const navDisplay = formatNavValueDisplay(
          row.navValue != null && row.navValue !== ""
            ? row.navValue
            : navNum.toFixed(4),
        );
        const dateSerial = navDateToExcelSerial(row.navDate || row.date);
        const dateVal =
          dateSerial != null ? dateSerial : row.navDate || row.date || "";
        return [dateVal, displayName, displaySfin, navDisplay];
      });
      const sheetData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      applyExcelDateFormat(ws, 0, rows.length);
      ws["!cols"] = [{ wch: 12 }, { wch: 30 }, { wch: 16 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "NAV Details");
      const firstDate = sorted[0]?.navDate || sorted[0]?.date;
      const lastDate =
        sorted[sorted.length - 1]?.navDate || sorted[sorted.length - 1]?.date;
      const fileName = `NAV_All_Funds_${formatNavDateForFilename(firstDate)}_to_${formatNavDateForFilename(lastDate)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      return;
    }
    const fundDisplayName =
      selectedFundLabel ||
      fundOptions.find((f) => f.value === fundName)?.label ||
      "Fund";
    const lastOpenParen = fundDisplayName.lastIndexOf("(");
    const lastCloseParen = fundDisplayName.lastIndexOf(")");
    const hasParentheticalSfin =
      lastOpenParen > 0 && lastCloseParen === fundDisplayName.length - 1;
    const cleanFundName = hasParentheticalSfin
      ? fundDisplayName.slice(0, lastOpenParen).trim()
      : fundDisplayName;
    const sfinValue = hasParentheticalSfin
      ? fundDisplayName.slice(lastOpenParen + 1, lastCloseParen).trim()
      : "";
    const headers = ["Nav Date", "Fund Name", "SFIN", "NAV"];
    const rows = chartData.map((row) => {
      const dateSerial = navDateToExcelSerial(row.navDateRaw || row.date);
      const dateVal =
        dateSerial != null ? dateSerial : row.navDateRaw || row.date || "";
      const navDisplay = formatNavValueDisplay(
        row.navValueOriginal != null && row.navValueOriginal !== ""
          ? String(row.navValueOriginal)
          : typeof row.nav === "number"
            ? row.nav.toFixed(4)
            : String(row.nav),
      );
      return [dateVal, cleanFundName, sfinValue, navDisplay];
    });
    const sheetData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    applyExcelDateFormat(ws, 0, rows.length);
    const colWidths = [
      { wch: 12 },
      { wch: Math.max(20, fundDisplayName.length + 2) },
      { wch: Math.max(20, sfinValue.length + 2) },
      { wch: 12 },
    ];
    ws["!cols"] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "NAV Details");
    const fileName = `NAV_${fundDisplayName.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 40)}_${formatNavDateForFilename(chartData[0]?.navDateRaw)}_to_${formatNavDateForFilename(chartData[chartData.length - 1]?.navDateRaw)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Chart title: fund name only (no sFIN); dropdown keeps "Fund Name (sFIN)"
  const chartTitle = React.useMemo(() => {
    if (!selectedFundLabel) return "";
    return selectedFundLabel || "";
  }, [selectedFundLabel]);

  return (
    <div className="nav-fund-details-wrapper">
    
      <Modal
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        title="No Data Available for Download"
        type="error"
        btnTitle="Close"
      >
        <p>Please select a valid fund and date range.</p>
      </Modal>
      {loading && (
        <div className="loader-overlay">
          <BrandLoader color="#97262A" loading={loading} size={15} />
        </div>
      )}
      <h3 className="nav-fund-details-title bold">NAV</h3>
      <p className="regular-body regular Secondary-Grey-Shades-900 text-center">
        Get the latest Net Asset Value for all funds
      </p>
      <div className="nav-fund-details-card">
        <div className="nav-fund-details-panel">
          <div className="form-section">
            <span className="section-label">Category</span>
            <div className="radio-group">
              <label
                className={`radio-option ${category === CATEGORY_OPEN ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="category"
                  value={CATEGORY_OPEN}
                  checked={category === CATEGORY_OPEN}
                  onChange={(e) => setCategory(e.target.value)}
                />
                <span className="radio-label">Open</span>
              </label>
              <label
                className={`radio-option ${category === CATEGORY_TRADITIONAL ? "selected" : ""} `}
              >
                <input
                  type="radio"
                  name="category"
                  value={CATEGORY_TRADITIONAL}
                  checked={category === CATEGORY_TRADITIONAL}
                  onChange={(e) => setCategory(e.target.value)}
                />
                <span className="radio-label">Traditional</span>
              </label>
            </div>
          </div>

          {showGroupBy && (
            <div className="form-section group-by-section">
              <label className="section-label label-one m-0">Group By</label>
              <div className="checkbox-group">
                {groupByOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="checkbox-option label-one m-0"
                  >
                    <input
                      type="checkbox"
                      checked={groupBySelected.includes(opt.value)}
                      onChange={() => toggleGroupBy(opt.value)}
                    />
                    <span className="checkbox-label">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="form-section">
            <FormSelect
              label="Fund Name"
              value={fundName}
              onChange={(e) => setFundName(e.target.value)}
              options={fundOptions}
              placeholder={fundsLoading ? "Loading…" : "Select Fund Name"}
              disabled={fundsLoading}
            />
          </div>
          <div className="form-selctdob-box">
            <div className="form-section date-range">
              <label className="section-label label-one m-0">From</label>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                selected={dateFrom}
                onChange={setDateFrom}
                dateFormat="dd/MM/yyyy"
                className="nav-date-input"
                placeholderText="Select date"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                maxDate={today()}
                todayButton="Today"
                onChangeRaw={(e) => {
                  if (!e || !e.target || typeof e.target.value !== 'string') return;
                  
                  let value = e.target.value.replace(/\D/g, "");
              
                  if (value.length > 2) {
                    value = value.slice(0, 2) + "/" + value.slice(2);
                  }
              
                  if (value.length > 5) {
                    value = value.slice(0, 5) + "/" + value.slice(5, 9);
                  }
              
                  e.target.value = value;
                }}
              />
            </div>
            <div className="form-section date-range">
              <label className="section-label label-one m-0">To</label>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                selected={dateTo}
                onChange={setDateTo}
                dateFormat="dd/MM/yyyy"
                className="nav-date-input"
                placeholderText="Select date"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                maxDate={today()}
                todayButton="Today"
                onChangeRaw={(e) => {
                  if (!e || !e.target || typeof e.target.value !== 'string') return;
                  let value = e.target.value.replace(/\D/g, "");
              
                  if (value.length > 2) {
                    value = value.slice(0, 2) + "/" + value.slice(2);
                  }
              
                  if (value.length > 5) {
                    value = value.slice(0, 5) + "/" + value.slice(5, 9);
                  }
              
                  e.target.value = value;
                }}
              />
            </div>
          </div>

          <div className="nav-fund-actions">
            <button
              type="button"
              className="btn nav-btn-outline secondary-button regular"
              onClick={handleExcel}
            >
              Excel
            </button>
            <button
              type="button"
              className="btn nav-btn-primary primary-button regular "
              onClick={handleGo}
              disabled={loading}
            >
              {loading ? "Loading…" : "Go"}
            </button>
          </div>
        </div>

        <div className="nav-fund-chart-area">
          {loading && (
            <div className="nav-chart-loading">
              <span>Loading…</span>
            </div>
          )}
          {!loading && isAllFundsValue(fundName) && (
            <div className="nav-all-funds-placeholder">
              <div className="nav-all-funds-placeholder-image">
                <svg
                  width="120"
                  height="120"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                    stroke="var(--primary-Burgundy-Tint-800, #97262a)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
                    stroke="var(--primary-Burgundy-Tint-800, #97262a)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="nav-all-funds-placeholder-text">
                Please download report to show all funds details for the
                selected date range.
              </p>
              <p className="nav-all-funds-placeholder-hint">
                Select date range, click Go to load data, then use the Excel
                button to download.
              </p>
            </div>
          )}
          {!loading && !isAllFundsValue(fundName) && chartData.length > 0 && (
            <>
              <h3 className="nav-chart-title">{chartTitle}</h3>
              <div className="nav-chart-scroll">
                <BarChart
                  width={Math.max(400, chartData.length * 44)}
                  height={320}
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e0e0e0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v}
                  />
                  <YAxis
                    domain={[0, "auto"]}
                    tick={{ fontSize: 11 }}
                    allowDecimals
                    tickFormatter={(v) => formatNavValueDisplay(String(v)) || v}
                  />
                  <Tooltip
                    formatter={(value, name, props) => {
                      const raw =
                        props?.payload?.navValueOriginal != null &&
                        props.payload.navValueOriginal !== ""
                          ? String(props.payload.navValueOriginal)
                          : value;
                      const display = formatNavValueDisplay(raw) || value;
                      return [
                        display,
                        category === CATEGORY_TRADITIONAL
                          ? "NAV (Rs in Cr.)"
                          : "NAV",
                      ];
                    }}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <ReferenceLine
                    y={chartData.length ? chartData[0]?.nav : 0}
                    stroke="var(--primary-Burgundy-Tint-800)"
                    strokeDasharray="4 4"
                  />
                  <Bar
                    dataKey="nav"
                    fill="rgba(151, 38, 42, 0.6)"
                    radius={[2, 2, 0, 0]}
                    name={
                      category === CATEGORY_TRADITIONAL
                        ? "NAV (Rs in Cr.)"
                        : "NAV"
                    }
                  />
                </BarChart>
              </div>
              <p className="nav-chart-disclaimer">
                NAV (Net Asset Value) is the value per unit of the fund. Past
                NAV does not indicate future performance. This information is
                for reference only and not investment advice.
              </p>
            </>
          )}
          {!loading && !isAllFundsValue(fundName) && chartData.length === 0 && (
            <div className="nav-chart-empty">
              {chartError ? (
                <p className="nav-chart-error">{chartError}</p>
              ) : (
                <p>
                  Select a fund and date range, then click Go to view NAV chart.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <footer className="nav-fund-footer">
        <p className="nav-footer-regulatory">
          Insurance Regulatory and Development Authority vide its circular
          bearing reference no. IRDA/F&I/CIR/INV/173/08/2011 dated July 29, 2011
          provided Segregated Fund Identification Number (SFIN) to be allotted
          to the fund options. The existing fund/funds of your policy is now
          aligned to the new fund structure.
        </p>
        <p className="nav-footer-disclaimer">
          Please note that this exercise does not lead to any change in your
          fund value or your investment objectives. Also your associated Fund
          Management Charges would not increase.
        </p>
      </footer>
    </div>
  );
};

export default NavFundDetailsForm;
